/* The "/" block menu catalogue — PURE, no TipTap import.
 *
 * Each item's `command({ editor, range })` is a plain closure that runs against
 * whatever editor is handed to it, so this file needs no TipTap types and stays
 * unit-testable (see the note in editorCapabilities.js).
 *
 * A command must delete the typed "/query" (deleteRange) before inserting its
 * block, or the slash text is left behind in the document.
 */

import { SCRIPT_ELEMENTS } from '../../services/scriptFormat';

/* Script elements, as "/" entries.
 *
 * Tab is the fast path and the one worth learning, but a feature reachable ONLY
 * by a keystroke nobody mentioned is a feature nobody finds — phase 2 shipped
 * nesting that worked perfectly and was invisible. These rows are how you
 * discover the elements exist; the hint tells you about Tab.
 *
 * Generated from the same catalogue that drives the keymap, so the menu cannot
 * list an element the editor doesn't have.
 */
const SCRIPT_SLASH_ITEMS = SCRIPT_ELEMENTS.map((el) => ({
	title: el.label,
	hint: el.hint,
	icon: el.icon,
	keywords: [...el.keywords, 'script'],
	requires: 'script',
	command: ({ editor, range }) =>
		editor.chain().focus().deleteRange(range).setNode(el.name).run(),
}));

export const SLASH_ITEMS = [
	{
		title: 'Heading 1',
		hint: 'Big section title',
		icon: 'fa-heading',
		keywords: ['h1', 'title', 'heading'],
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run(),
	},
	{
		title: 'Heading 2',
		hint: 'Section title',
		icon: 'fa-heading',
		keywords: ['h2', 'subtitle', 'heading'],
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run(),
	},
	{
		title: 'Heading 3',
		hint: 'Sub-section',
		icon: 'fa-heading',
		keywords: ['h3', 'heading'],
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run(),
	},
	{
		title: 'Bulleted list',
		hint: 'Simple bullets',
		icon: 'fa-list-ul',
		keywords: ['bullet', 'ul', 'list', 'unordered'],
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).toggleBulletList().run(),
	},
	{
		title: 'Numbered list',
		hint: 'Ordered steps',
		icon: 'fa-list-ol',
		keywords: ['number', 'ol', 'ordered', 'list'],
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
	},
	{
		title: 'To-do list',
		hint: 'Checkboxes',
		icon: 'fa-square-check',
		keywords: ['todo', 'task', 'checkbox', 'check'],
		requires: 'tasks',
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).toggleTaskList().run(),
	},
	{
		title: 'Quote',
		hint: 'Pull out a passage',
		icon: 'fa-quote-left',
		keywords: ['quote', 'blockquote', 'citation'],
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).toggleBlockquote().run(),
	},
	{
		title: 'Code block',
		hint: 'Monospaced block',
		icon: 'fa-code',
		keywords: ['code', 'snippet', 'pre'],
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
	},
	{
		title: 'Callout',
		hint: 'Highlighted note',
		icon: 'fa-circle-info',
		keywords: ['callout', 'note', 'info', 'aside', 'warning'],
		requires: 'callout',
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).setCallout().run(),
	},
	{
		title: 'Divider',
		hint: 'Horizontal rule',
		icon: 'fa-minus',
		keywords: ['divider', 'hr', 'rule', 'separator', 'line'],
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
	},
	{
		title: 'Table of contents',
		hint: 'Auto-lists this doc’s headings',
		icon: 'fa-list-tree',
		keywords: ['toc', 'contents', 'outline', 'index'],
		requires: 'toc',
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).setTableOfContents().run(),
	},
	/* Inline sub-page: a child document embedded in this one's body, Notion-style.
	   The list-view tree shows the same relationship, but nesting a page WHERE
	   YOU ARE WRITING is what makes a doc a workspace rather than a file. */
	{
		title: 'Sub-page',
		hint: 'Create a page inside this one',
		icon: 'fa-file-lines',
		keywords: ['page', 'subpage', 'sub-page', 'child', 'nest', 'nested'],
		requires: 'childPages',
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).insertNewChildPage().run(),
	},
	/* Table blocks. Production paperwork lives in tables, so inserting AND
	   restructuring one has to be reachable mid-flow — a crew document grows a
	   column the day before load-in, not at template time. */
	{
		title: 'Table',
		hint: '3×3 with a header row',
		icon: 'fa-table',
		keywords: ['table', 'grid', 'sheet', 'rows', 'columns'],
		requires: 'tables',
		command: ({ editor, range }) =>
			editor
				.chain()
				.focus()
				.deleteRange(range)
				.insertTable({ rows: 3, cols: 3, withHeaderRow: true })
				.run(),
	},
	{
		title: 'Add column',
		hint: 'Insert a column after this one',
		icon: 'fa-table-columns',
		keywords: ['column', 'col', 'add', 'insert'],
		requires: 'tables',
		requiresContext: 'inTable',
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).addColumnAfter().run(),
	},
	{
		title: 'Add row',
		hint: 'Insert a row below',
		icon: 'fa-table-rows',
		keywords: ['row', 'add', 'insert'],
		requires: 'tables',
		requiresContext: 'inTable',
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).addRowAfter().run(),
	},
	{
		title: 'Delete row',
		hint: 'Remove the current row',
		icon: 'fa-eraser',
		keywords: ['delete row', 'remove row'],
		requires: 'tables',
		requiresContext: 'inTable',
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).deleteRow().run(),
	},
	{
		title: 'Delete column',
		hint: 'Remove the current column',
		icon: 'fa-eraser',
		keywords: ['delete column', 'remove column'],
		requires: 'tables',
		requiresContext: 'inTable',
		command: ({ editor, range }) =>
			editor.chain().focus().deleteRange(range).deleteColumn().run(),
	},
	...SCRIPT_SLASH_ITEMS,
];

/* Filter by capability, then by CONTEXT, then by the typed query.
 *
 * The context pass exists because a menu that offers an action which silently
 * does nothing is worse than one that omits it: "Add column" outside a table
 * deleted the typed "/column" and then no-opped, which reads as the feature
 * being broken. An item that cannot apply right now is not shown at all.
 */
export function filterSlashItems(items, query, capabilities = {}, context = {}) {
	const enabled = items.filter((item) => {
		if (item.requires && !capabilities[item.requires]) return false;
		if (item.requiresContext && !context[item.requiresContext]) return false;
		return true;
	});
	const q = (query || '').toLowerCase().trim();
	if (!q) return enabled;

	/* RANK, don't just filter. Catalogue order alone meant typing "table" hit
	   "Table of contents" before "Table" — the exact match lost to a longer
	   title that merely contained it. Exactness wins, then title matches, then
	   keyword aliases; ties keep catalogue order. */
	const score = (item) => {
		const title = item.title.toLowerCase();
		if (title === q) return 0;
		if (title.startsWith(q)) return 1;
		if (title.includes(q)) return 2;
		const kws = item.keywords || [];
		if (kws.some((k) => k === q)) return 3;
		if (kws.some((k) => k.startsWith(q))) return 4;
		if (kws.some((k) => k.includes(q))) return 5;
		return Infinity;
	};

	return enabled
		.map((item, i) => ({ item, s: score(item), i }))
		.filter((r) => r.s !== Infinity)
		.sort((a, b) => a.s - b.s || a.i - b.i)
		.map((r) => r.item);
}
